import { useEffect, useState } from "react";
import Svg1 from "@assets/design/wizard-1-security.svg?react";
import Svg2 from "@assets/design/wizard-2-unified-platform.svg?react";
import Svg3 from "@assets/design/wizard-3-spacial-alignment.svg?react";
import Svg4 from "@assets/design/wizard-4-data-integration.svg?react";
import { useMediaQueryList } from "@hooks/use-media-query";
import { Box, Stack } from "@mui/material";
import { sphereColors } from "@styles/common-colors";

const NUM_PAGES = 4;
// Jump to the next page every 60 seconds.
const PAGE_INTERVAL_MSEC = 60000;

/**
 * The left panel of the wizard view.
 * Contains pages with various info texts. They get switched in a time interval. The user can also click on
 * the page buttons.
 */
export function WizardLeftPanel(): JSX.Element {
  const { isScreenXsAndSmall } = useMediaQueryList();
  const [page, setPage] = useState(0);

  // Jump to the next page every N seconds.
  // By adding `page` to the dependency array, we ensure that the timer is restarted whenever the user clicks on a page button.
  // Using `setInterval` brings no real advantage, since we're anyways clearing the timer after each page transition.
  useEffect(() => {
    const interval = setInterval(() => {
      setPage((page) => (page + 1) % NUM_PAGES);
    }, PAGE_INTERVAL_MSEC);
    return () => clearInterval(interval);
  }, [page]);

  let icon: JSX.Element;
  let title: string;
  let desc: string;

  if (page === 0) {
    icon = <Svg1/>;
    title = "Enterprise-grade security";
    desc = "Your data is being transferred to our highly secure cloud environment, protected by SOC2 Type 2 certified infrastructure. " +
      "All files are encrypted both during transfer and when stored, keeping your information safe and confidential.";
  } else if (page === 1) {
    icon = <Svg2/>;
    title = "FARO’s unified reality platform";
    desc = "Sphere XG brings all your reality capture data together in one powerful platform. " +
      "Our advanced algorithms enhance your data for superior visualization and deeper insights from your field captures.";
  } else if (page === 2) {
    icon = <Svg3/>;
    title = "Precise spatial alignment";
    desc = "Our smart registration technology precisely aligns your scans in 3D space, even with minimal overlap. " +
      "This automated process guarantees accurate measurements across your entire dataset, " +
      "delivering a seamless digital representation of your physical site.";
  } else {
    icon = <Svg4/>;
    title = "Seamless data integration";
    desc = "All your input data is combined into a unified representation for easy access. " +
      "This integration lets you seamlessly explore the entire dataset in the Sphere XG Viewer, " +
      "ensuring a smooth and continuous experience across your captured reality.";
  }

  return (
    <Stack sx={{
      minWidth: isScreenXsAndSmall ? "100%" : "380px",
      width: isScreenXsAndSmall ? "100%" : "380px",
      height: "100%",
      paddingTop: "30px",
      paddingBottom: "26px",
      paddingLeft: "30px",
      paddingRight: "30px",
      backgroundColor: sphereColors.pureWhite,
      border: `1px solid ${sphereColors.gray200}`,
    }}>
      <Box sx={{ textAlign: "center", overflowX: "clip" }}>
        {icon}
      </Box>

      <Box sx={{
        marginTop: "30px",
        fontSize: "20px",
        fontWeight: "600",
      }}>
        {title}
      </Box>

      <Box sx={{
        marginTop: "10px",
        fontSize: "14px",
      }}>
        {desc}
      </Box>

      {/* Rectangular page buttons */}
      <Box sx={{
        height: "100%",
        display: "flex",
        alignItems: "end",
        marginTop: "40px",
      }}>
        <Stack
          direction="row"
          gap="10px"
          sx={{
            width: "100%",
            height: "16px",
            justifyContent: "center",
          }}
        >
          {/* We need a second Box element to increase the vertical size of the button's hitbox. */}
          <Box
            data-testid="sa-wlp-page0"
            sx={{
              width: "25%",
              height: "100%",
              paddingTop: "6px",
              paddingBottom: "6px",
              cursor: "pointer",
            }}
            onClick={() => setPage(0)}
          >
            <Box sx={{
              width: "100%",
              height: "100%",
              backgroundColor: page === 0 ? sphereColors.blue500 : sphereColors.gray200,
            }} />
          </Box>

          <Box
            data-testid="sa-wlp-page1"
            sx={{
              width: "25%",
              height: "100%",
              paddingTop: "6px",
              paddingBottom: "6px",
              cursor: "pointer",
            }}
            onClick={() => setPage(1)}
          >
            <Box sx={{
              width: "100%",
              height: "100%",
              backgroundColor: page === 1 ? sphereColors.blue500 : sphereColors.gray200,
            }} />
          </Box>

          <Box
            data-testid="sa-wlp-page2"
            sx={{
              width: "25%",
              height: "100%",
              paddingTop: "6px",
              paddingBottom: "6px",
              cursor: "pointer",
            }}
            onClick={() => setPage(2)}
          >
            <Box sx={{
              width: "100%",
              height: "100%",
              backgroundColor: page === 2 ? sphereColors.blue500 : sphereColors.gray200,
            }} />
          </Box>

          <Box
            data-testid="sa-wlp-page3"
            sx={{
              width: "25%",
              height: "100%",
              paddingTop: "6px",
              paddingBottom: "6px",
              cursor: "pointer",
            }}
            // eslint-disable-next-line @typescript-eslint/no-magic-numbers -- Page number
            onClick={() => setPage(3)}
          >
            <Box sx={{
              width: "100%",
              height: "100%",
              // eslint-disable-next-line @typescript-eslint/no-magic-numbers -- Page number
              backgroundColor: page === 3 ? sphereColors.blue500 : sphereColors.gray200,
            }} />
          </Box>
        </Stack>
      </Box>
    </Stack>
  );
}
